import h from 'hyperscript';

import clearIcon from '@disy/cadenza-icons/clear.svg';
import replaceImage from '@disy/cadenza-icons/dragdrop-replace.svg';
import plusIcon from '@disy/cadenza-icons/plus.svg';
import settingsIcon from '@disy/cadenza-icons/settings-console.svg';
import type { EventKey } from '@disy/lib/event-util';
import { on, unByKey } from '@disy/lib/event-util';
import { isEqual } from '@disy/lib/is-equal';
import { logger } from '@disy/lib/logging';
import { uniqueId } from '@disy/lib/unique-id';
import type { HyperScriptButtonOptions } from '@disy/ui/button';
import { createAnchorButton, createIconButton } from '@disy/ui/button';
import '@disy/ui/form-group';
import { icon } from '@disy/ui/icon';
import type { MenuContentElement } from '@disy/ui/menu';
import { setupMenuButton } from '@disy/ui/menu';
import { createMoreActionsButton } from '@disy/ui/more-actions-button';
import { getSetting } from '@disy/workbook.lib/settings';

import { getStore } from 'cadenza/store/cadenza-store';
import type { AttributeChooserChangeEvent } from 'cadenza/ui/attribute-chooser/attribute-chooser';
import { createAttributeChooser } from 'cadenza/ui/attribute-chooser/attribute-chooser';
import type { AttributeTile } from 'cadenza/ui/attribute-tile/attribute-tile';
import { COMPONENT_NAME as ATTRIBUTE_TILE_COMPONENT_NAME } from 'cadenza/ui/attribute-tile/attribute-tile';
import { setupDataTransfer } from 'cadenza/utils/datatransfer';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { COMMAND_UPDATE } from 'cadenza/workbook/event-types';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { ConditionSettingsKey } from 'cadenza/workbook/model/condition-settings-key.gen';
import { isReplaceModifierKeyDown } from 'cadenza/workbook/sortable-list/replace-attribute-plugin';
import { SortableList } from 'cadenza/workbook/sortable-list/sortable-list';
import { filterAllAttributesAndHierarchies } from 'cadenza/workbook/workbook-attribute-util';
import { getAllEnrichmentObjecttypeIds } from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type { ConditionsPanelController } from 'cadenza/workbook/workbook-panel/analysis-context/conditions-panel-controller';
import type { AttributeBundleValidationResult } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator';
import type { DataViewBundleController } from 'cadenza/workbook/workbook-panel/settings/attributes/data-view-bundle-controller';
import { getWorkbook } from 'cadenza/workbook/workbook-utils';

import './attribute-drop-panel.css';
import i18n from './attribute-drop-panel.properties';

const COMPONENT_NAME = 'd-attribute-drop-panel';

const REPLACE_OVERLAY_SELECTOR = '.attribute-replace-overlay';

/** Options for configuring this AttributeDropPanel. */
export interface AttributeDropPanelOptions {
  /**
   * This component has a real bad cutting as it includes the heading for different uses cases:
   * Usually (true) it looks like a form group (with a bold label on top),
   * but in the analysis context it looks like a panel section.
   * (Future improvement: The heading should not be part of this component.)
   */
  isPanelSectionHeading?: boolean;
  /**
   * The title to use in the heading. If `title` is not set, component will be without header.
   * This overrules the `showPlusButton` and `showMoreActionButton` flags since header will not be added.
   */
  title?: string;
  /** Text to show in the drop area if empty. */
  dropAreaText?: string;
  /** Additional style classes to pass to the sortable list. */
  sortableStyleClasses?: string | string[];
  /** Whether it's allowed to push items into the panel. */
  push?: boolean;
  /** Whether it's allowed to pull items from the panel and what the effect is. */
  pull?: 'move' | 'copy' | false;
  /** Indicates on which element dragging can be started. */
  handleSelector?: string;
  /** Whether the clear button to remove attributes is provided. */
  showClearButton?: boolean;
  /** Whether the plus button to add attributes is provided. */
  showPlusButton?: boolean;
  /** Promise to show the attribute settings dialogue for this bundle. If present, a button will be provided. */
  editBundleSettingsAction?: () => Promise<boolean> | null;
  /** Whether the more action buttons is provided. */
  showMoreActionButton?: boolean;
  /** If true, disables all drag&drop functionality. */
  dragAndDropDisabled?: boolean;
  /** If true, the replace plugin is enabled and attributes can be replaced by dropping over them while pressing the CTRL key. */
  enableReplace?: boolean;
  /** Selector to get draggable elements from the list. */
  draggableSelector?: string;
}

export class AttributeDropPanel extends HTMLElement {
  /** Data backend for this panel. */
  // should be protected, but has access in TableViewSettingsPanelContents
  readonly controller: DataViewBundleController;

  #plusButton?: HTMLButtonElement;
  #editBundleSettingsButton?: HTMLButtonElement;
  #moreActionsButton?: HTMLButtonElement;
  readonly #header;
  readonly #alert;
  readonly #dropArea: SortableList;
  // ! – initialized in `connectedCallback`
  #eventKeys!: EventKey[];
  // ! – initialized in `render`
  #attributes!: WorkbookAttributeDto[]; // currently rendered state
  readonly #sortableListOptions;

  constructor(
    controller: DataViewBundleController,
    {
      isPanelSectionHeading = false,
      title,
      dropAreaText = controller.dropPanelPlaceholderMessage ?? i18n('droparea-text'),
      sortableStyleClasses = [],
      push = true,
      pull = 'move',
      handleSelector,
      showClearButton = false,
      showPlusButton = true,
      editBundleSettingsAction = undefined,
      showMoreActionButton = false,
      dragAndDropDisabled = false,
      enableReplace = false,
      draggableSelector = 'li',
    }: AttributeDropPanelOptions = {},
  ) {
    super();

    if (typeof sortableStyleClasses === 'string') {
      sortableStyleClasses = [sortableStyleClasses];
    }

    this.controller = controller;
    this.#sortableListOptions = {
      push,
      pull,
      group: 'attributes-list',
      handleSelector,
      dropAreaText,
      styleClasses: sortableStyleClasses,
      disabled: dragAndDropDisabled,
      enableReplace,
      draggableSelector,
    };
    this.#dropArea = this._createSortableList(this.controller.attributes);
    if (title) {
      this.#header = this._createHeader(
        isPanelSectionHeading,
        title,
        showClearButton,
        showPlusButton,
        showMoreActionButton,
        editBundleSettingsAction,
      );
      this.append(this.#header);
    }
    this.append(this.#dropArea);

    if (!isPanelSectionHeading) {
      this.#alert = h('.d-alert.d-alert-small');
      this.#dropArea.after(this.#alert);
      this._updateAlert();
    }
  }

  _createHeader(
    isPanelSectionHeading: boolean,
    title: string,
    showClearButton: boolean,
    showPlusButton: boolean,
    showMoreActionButton: boolean,
    editBundleSettingsAction?: () => Promise<boolean> | null,
  ) {
    if (isPanelSectionHeading) {
      const buttonsPanel = h('.d-stack-h.space-1');
      this._createDropPanelButtons(
        buttonsPanel,
        title,
        isPanelSectionHeading,
        showClearButton,
        showPlusButton,
        showMoreActionButton,
        editBundleSettingsAction,
      );
      const id = uniqueId();
      this.setAttribute('aria-labelledby', id);
      return h(`.${COMPONENT_NAME}--header.d-hover-context.d-stack-h`, [
        h(`h3.${COMPONENT_NAME}-header--heading`, { id }, title),
        buttonsPanel,
      ]);
    } else {
      this.classList.add('form-group');
      // This ID is necessary for the following reason: the first element in a label is treated
      // special if it is an icon (which is the case here). This will lead to weird highlighting
      // issues when hovering over the button or the label.
      const id = uniqueId();
      const buttonsPanel = h('.d-attribute-drop-panel--buttons', { id });
      this._createDropPanelButtons(
        buttonsPanel,
        title,
        isPanelSectionHeading,
        showClearButton,
        showPlusButton,
        showMoreActionButton,
        editBundleSettingsAction,
      );
      return h('label', { attrs: { for: id } }, [buttonsPanel, title]);
    }
  }

  _createDropPanelButtons(
    buttonsPanel: HTMLElement,
    title: string,
    isPanelSectionHeading: boolean,
    showClearButton: boolean,
    showPlusButton: boolean,
    showMoreActionButton: boolean,
    editBundleSettingsAction?: () => Promise<boolean> | null,
  ) {
    const buttonOptions: HyperScriptButtonOptions = { variant: 'borderless', size: isPanelSectionHeading ? 's' : 'xs' };
    if (showClearButton) {
      const clearButton = createIconButton(icon(clearIcon), i18n('button.clearAll'), {
        ...buttonOptions,
        styleClass: ['is-hover-visible', 'd-condition-drop-panel--clear'],
        // cast ok, because we only show this button if we are a condition panel
        onclick: () => {
          (this.controller as unknown as ConditionsPanelController).clearAll();
          clearButton.hidden = true;
        },
      });
      buttonsPanel.append(clearButton);
    }
    if (showMoreActionButton && this.hasEnabledMoreActionsButton()) {
      this.#moreActionsButton = createMoreActionsButton(title, buttonOptions);
      setupMenuButton(this.#moreActionsButton, this.getMoreActions.bind(this));
      buttonsPanel.append(this.#moreActionsButton);
    }
    if (showPlusButton && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-view-content')) {
      const label = this.isDropElementAttribute ? i18n('button.plus.attribute') : i18n('button.plus.condition');
      let attrs = {
        'aria-haspopup': 'menu',
        'aria-label': `${title} – ${label}`,
      };
      this.#plusButton = createIconButton(icon(plusIcon), label, { ...buttonOptions, attrs });
      this.#plusButton.disabled = !this.controller.canAcceptMoreAttributes();
      buttonsPanel.append(this.setupPlusButton(this.#plusButton));
    }
    if (editBundleSettingsAction) {
      this.#editBundleSettingsButton = createIconButton(icon(settingsIcon), i18n('button.settings'), {
        ...buttonOptions,
        styleClass: 'settings',
      });
      on(this.#editBundleSettingsButton, 'click', () => {
        editBundleSettingsAction()?.catch(logger.error);
      });
      buttonsPanel.append(this.#editBundleSettingsButton);
    }
  }

  setupPlusButton(plusButton: HTMLButtonElement): HTMLElement {
    return createAttributeChooser(
      {
        provider: () => filterAllAttributesAndHierarchies((attribute) => this.controller.accepts(attribute).accepts),
        onchange: (e: AttributeChooserChangeEvent) => {
          const attributeId = e.detail.attribute.id as AttributeId;
          this.controller.add(attributeId);
        },
        enrichmentObjecttypeIds: getAllEnrichmentObjecttypeIds(getStore().getState()),
      },
      plusButton,
    );
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);

    // initialize attributes to be used in "render"
    this.#attributes = this.controller.attributes;
    this.#eventKeys = [on(getWorkbook(), COMMAND_UPDATE, () => this.render())];
  }

  disconnectedCallback() {
    unByKey(this.#eventKeys);
  }

  render() {
    /**
     * #IE11 workaround. Sometimes COMMAND_UPDATE callback is called after attribute-drop-panel
     * (and it's listener) was removed. This if() double-checks if render() should be called.
     *
     * The problem with calling removed listener is a IE11 bug
     * https://stackoverflow.com/questions/41959536/event-listener-function-called-after-it-was-removed-in-ie11
     */
    if (document.body.contains(this)) {
      const attributes = this.controller.attributes;
      // In a chart dimensions impact measure alert and vice versa, so execute even if attributes are the same
      this._updateAlert();

      const previousAttributes = this.#attributes ?? [];
      if (!this.needsReRendering(previousAttributes, attributes)) {
        return;
      }

      let previousElements: HTMLElement[];

      let lastIndex: number;
      previousElements = this._itemElements;
      previousAttributes.forEach((previousAttribute, i) => {
        const index = attributes.findIndex((a) => a.id === previousAttribute.id);
        // Note: if the element was already removed, i may go over the array's bounds.
        if (
          i < previousElements.length &&
          (index === -1 || index < lastIndex || attributes[index] !== previousAttribute)
        ) {
          previousElements[i].remove();
        }
        lastIndex = index;
      });

      const dropArea = this.#dropArea;
      previousElements = this._itemElements;
      attributes.forEach((attribute, i) => {
        if (!previousElements.some((el) => el.dataset.id === attribute.id)) {
          dropArea.insertBefore(this.controller.createElement(attribute), dropArea.children[i]);
        }
      });

      this.#attributes = attributes;

      dropArea.checkDropArea();
      if (this.#plusButton) {
        this.#plusButton.disabled = !this.controller.canAcceptMoreAttributes();
      }
      this.postRender();
    }
  }

  /** Executed after the `render` call */
  postRender() {
    /* override in subclasses if necessary */
  }

  /**
   * Analyses the attributes and decide if the rendering request could be ignored.
   */
  needsReRendering(currentRenderAttributes: WorkbookAttributeDto[], attributes: WorkbookAttributeDto[]) {
    /* If the attributes stayed the same we don't trigger re-render. This is not true for:
     * the case when there is Top N configured for the attribute in the drop panel. The reason for that is that
     *  the modification of measure bundle could change what should be rendered for that attribute. (Depending on
     * the fact if Top N configuration stayed valid)
     * the case when there is animation capable attribute because those should be rendered for new change in DataViews
     */
    const isThereAttributeWithTopNConfigured = attributes.some((attribute) =>
      getSetting(attribute, AttributeModificationKey.TOP_N_ROWS_ENABLED),
    );
    const isThereAnimationCapableAttribute = attributes.some((attribute) =>
      getSetting(attribute, ConditionSettingsKey.toPersistenceKey('CONDITION_ANIMATION_ALLOW')),
    );
    return (
      !isEqual(currentRenderAttributes, attributes) ||
      isThereAttributeWithTopNConfigured ||
      isThereAnimationCapableAttribute
    );
  }

  // Ideally, the whole situation of condition-things sub-classing attribute-things should not exist.
  // Until we achieve this, this method provides some explicit way to ask on what we are operating on.
  protected get isDropElementAttribute() {
    return true;
  }

  /** This method has to be overridden, if a more actions menu should be displayed at the drop panel. */
  protected getMoreActions(): MenuContentElement[] {
    return [];
  }

  /**
   * This method has to be overridden to decide when the actions provided by getMoreActions are available.
   *
   * @return true (usually) if there is at least one not disabled action
   */
  protected hasEnabledMoreActionsButton() {
    return false;
  }

  _createSortableList(items: WorkbookAttributeDto[]) {
    const {
      pull,
      push,
      group,
      dropAreaText,
      handleSelector,
      styleClasses,
      disabled,
      enableReplace,
      draggableSelector,
    } = this.#sortableListOptions;
    const sortable = new SortableList({
      push,
      pull,
      group,
      handleSelector,
      disabled,
      enableReplace,
      draggableSelector,
    });
    sortable.classList.add('d-sortable-list--droparea');
    sortable.dataset.dropareaText = dropAreaText;
    on(sortable, 'add', (e: Event & { item: HTMLElement }) => this._onAddItemElement(e));
    on(sortable, 'update', (e) => this._onChangeItemElement(e));
    on(sortable, 'push', (e: CustomEvent) => this._checkIfCanBeDropped(e));
    on(sortable, 'dragOverReplace', (e: CustomEvent) => this._onDragoverReplace(e));
    on(sortable, 'replace', (e: CustomEvent) => this._onReplace(e));

    setupDataTransfer(sortable, (event) => ({ attributeId: (event.target as HTMLElement).dataset.id }));

    for (const styleClass of styleClasses) {
      sortable.classList.add(styleClass);
    }

    sortable.append(...items.map((item) => this.controller.createElement(item)));
    return sortable;
  }

  _onAddItemElement(event: Event & { item: HTMLElement }) {
    if (event.defaultPrevented) {
      return;
    }

    event.stopPropagation();

    const itemElement = event.item;
    const index = this._itemElements.indexOf(itemElement);
    const attributeId = itemElement.dataset.id as AttributeId;

    this.controller.add(attributeId, index);
    this.#dropArea.checkDropArea();

    /* In case adding attribute was a fail (because backend fails), then we need to remove the added
     * element from attribute bundle (in UI), because the store is unchanged and therefore data view
     * will not be refreshed.
     * In case adding attribute was successful then the code here doesn't matter much anyway because
     * store will be updated soon and dataView will be refreshed. Ideally this code should be run
     * on controller.add fail event, but we don't have the event for now */
    itemElement.remove();
  }

  _onChangeItemElement(event: Event) {
    event.stopPropagation();
    this.controller.reorder(
      this._itemElements.map((element) => (element as HTMLElement).dataset.id as ActiveAttributeId),
    );
  }

  _onDragoverReplace(event: CustomEvent) {
    const replacedAttributeElement = event.detail.replacedElement;

    if (!replacedAttributeElement.querySelector(REPLACE_OVERLAY_SELECTOR)) {
      const overlay = h(REPLACE_OVERLAY_SELECTOR, [
        icon(replaceImage, { size: 'xs' }),
        i18n('droparea.dragover.overlay'),
      ]);
      replacedAttributeElement.append(overlay);

      on(
        replacedAttributeElement,
        'dragleave',
        () => {
          removeReplaceOverlay(replacedAttributeElement);
        },
        { once: true },
      );
    }
  }

  _onReplace(event: CustomEvent) {
    const replacedAttributeElement = event.detail.replacedElement;
    const replacedAttributeId = replacedAttributeElement.getAttribute('data-id');

    const addedAttributeElement = event.detail.draggedElement;
    const addedAttributeId = addedAttributeElement.getAttribute('data-id');

    // added element is already in the DOM at this point, so we need to filter it out
    const currentItems = this._itemElements.filter((it) => it !== addedAttributeElement);
    const index = currentItems.indexOf(replacedAttributeElement);
    this.controller.replace(replacedAttributeId, addedAttributeId, index);

    replacedAttributeElement.remove();
  }

  _checkIfCanBeDropped(pushEvent: CustomEvent) {
    // If replace mode is enabled and modifier key is down, then the limit should not be checked
    // since the item would be replaced, and it would not change the total number of items in the bundle
    const replaceModeEnabled = this.#sortableListOptions.enableReplace;
    const replacedModifierKeyDown = isReplaceModifierKeyDown(pushEvent.detail.dragEvent);
    const enforceAttributesNumberLimit = !(replaceModeEnabled && replacedModifierKeyDown);

    const dropCandidate = pushEvent.detail.item.dataset.id;
    const attribute = this.controller.get(dropCandidate);
    const validationResult = this.controller.accepts(attribute, enforceAttributesNumberLimit);
    this._processDropMessage(validationResult);
    if (!validationResult.accepts) {
      this.#dropArea.dropDenied = true;
      pushEvent.preventDefault();
    }
  }

  _processDropMessage(
    { accepts, message = this.#dropArea.dataset.dropareaText! }: AttributeBundleValidationResult,
    dropArea = this.#dropArea,
  ) {
    const deniedMessageAttributeName = 'data-droparea-error-text';
    dropArea.removeAttribute(deniedMessageAttributeName);
    if (!accepts) {
      dropArea.setAttribute(deniedMessageAttributeName, message);
    }
  }

  _updateAlert() {
    const alertElement = this.#alert;
    if (alertElement) {
      const { hideDropArea, isOptional, message, action } = this.controller.getMessage();
      this.#dropArea.hidden = !!hideDropArea;
      alertElement.textContent = null;
      const alertTextElementId = uniqueId();
      if (message) {
        alertElement.append(h('span.d-alert--text', message, { id: alertTextElementId }));
      }
      alertElement.classList.toggle('d-alert-info', isOptional);
      alertElement.classList.toggle('d-alert-warning', !isOptional);
      /* Boolean(message) results false only if message is empty string. */
      this.#dropArea.classList.toggle('with-warning', !isOptional && Boolean(message));
      if (action) {
        const buttonId = uniqueId();
        alertElement.append(
          createAnchorButton(i18n('reverseOrder'), {
            id: buttonId,
            onclick: action,
            attrs: {
              'aria-labelledby': `${alertTextElementId} ${buttonId}`,
            },
          }),
        );
      }
    }
  }

  _hideDropArea(value: boolean) {
    this.#dropArea.hidden = value;
  }

  get _itemElements() {
    return [...this.#dropArea.querySelectorAll<HTMLElement>(':scope > [data-id]')];
  }

  get disabled() {
    return this.classList.contains('is-disabled');
  }

  set disabled(disabled: boolean) {
    this.classList.toggle('is-disabled', disabled);
    this.#dropArea.disabled = disabled;
    if (this.#plusButton) {
      this.#plusButton.disabled = disabled;
    }
  }

  lock(locked: boolean) {
    this.controller.locked = locked;
    if (locked) {
      this.setAttribute('locked', String(locked));
    } else {
      this.removeAttribute('locked');
    }
    if (this.#plusButton) {
      this.#plusButton.hidden = locked;
    }
    this.#dropArea.disabled = locked;
    const attributeTile = this.#dropArea.querySelector<AttributeTile>(ATTRIBUTE_TILE_COMPONENT_NAME);
    if (attributeTile) {
      attributeTile.locked = locked;
    }
  }

  accepts(attribute: WorkbookAttributeDto) {
    return this.controller.accepts(attribute);
  }
}

customElements.define(COMPONENT_NAME, AttributeDropPanel);

function removeReplaceOverlay(attributeElement: HTMLElement) {
  attributeElement.querySelector(REPLACE_OVERLAY_SELECTOR)?.remove();
}
